import {type FC, type PropsWithChildren, useEffect, useState} from 'react';
import {Navigate, useLocation} from 'react-router';

import {autorun, reaction} from 'mobx';

import {authStore} from '@yourcoach/shared/api/auth';
import {currentUserStore} from '@yourcoach/shared/api/user';
import {isUserHasRole} from '@yourcoach/shared/utils/entity/user';

import {useAppRedirect} from '@src/common/useAppRedirect';

import {getBrowserPath} from '../../v2/utils/getBrowserPath';
import {routerPath} from '../path';

export type Props = PropsWithChildren<{
  forCoach?: boolean;
  publicElement?: null | React.ReactNode;
}>;

export const PrivateRoute: FC<Props> = ({
  children,
  forCoach,
  publicElement,
}) => {
  const location = useLocation();
  const redirect = useAppRedirect();

  const loginPath = routerPath.login.$buildPath({});
  const userProfileRegPath = routerPath.userProfileReg.$buildPath({});
  const onboardingPath = routerPath.onboarding.$buildPath({});
  const rootPath = routerPath.$buildPath({});

  const [isAuthenticated, setIsAuthenticated] = useState<boolean | null>(null);
  const [needShowOnboarding, setNeedShowOnboarding] = useState(false);
  const [needShowChooseAccountType, setNeedShowChooseAccountType] =
    useState(false);

  useEffect(() => {
    const checkAuth = async () => {
      const sessionIsActive =
        (await authStore.sessionIsActive()) &&
        (await currentUserStore.userExists());

      setIsAuthenticated(sessionIsActive);
    };

    checkAuth();
  }, []);

  useEffect(() => {
    const dispose = autorun(() => {
      if (isAuthenticated && !currentUserStore.user) {
        setIsAuthenticated(false);
      }
    });

    return dispose;
  }, [isAuthenticated]);

  useEffect(() => {
    const dispose = autorun(() => {
      if (
        isAuthenticated &&
        forCoach &&
        !isUserHasRole('coach') &&
        !publicElement
      ) {
        redirect(routerPath.$buildPath({}));
      }
    });

    return dispose;
  }, [forCoach, isAuthenticated, publicElement, redirect]);

  useEffect(() => {
    const dispose = reaction(
      () => currentUserStore.user?.metadata?.onboarding,
      onboardingData => {
        const onboarding = onboardingData || {is_finished: true};

        setNeedShowOnboarding(!onboarding.is_finished);
        setNeedShowChooseAccountType(
          !onboarding.is_finished && !onboarding.account_type,
        );
      },
      {fireImmediately: true},
    );

    return dispose;
  }, []);

  if (isAuthenticated === null) {
    return null;
  }

  if (!isAuthenticated) {
    if (location.pathname !== loginPath) {
      if (publicElement) {
        return publicElement;
      }

      return (
        <Navigate replace state={{from: getBrowserPath()}} to={loginPath} />
      );
    }

    return null;
  }

  if (needShowChooseAccountType && !location.pathname.startsWith(loginPath)) {
    return (
      <Navigate
        replace
        state={{from: location.pathname}}
        to={`${loginPath}?nat=1`}
      />
    );
  }

  if (needShowOnboarding) {
    const accountType = (
      currentUserStore.user?.metadata?.onboarding || {
        account_type: 'client',
      }
    ).account_type;

    if (
      accountType === 'coach' &&
      !location.pathname.startsWith(userProfileRegPath)
    ) {
      return (
        <Navigate replace state={location.state} to={userProfileRegPath} />
      );
    }

    if (
      accountType !== 'coach' &&
      !location.pathname.startsWith(onboardingPath)
    ) {
      return <Navigate replace state={location.state} to={onboardingPath} />;
    }
  } else if (location.pathname.startsWith(onboardingPath)) {
    if (location.state?.from) {
      return (
        <Navigate replace state={location.state} to={location.state.from} />
      );
    } else {
      return <Navigate replace state={location.state} to={rootPath} />;
    }
  }

  return <>{children}</>;
};
